/** @module BarChartComponent */
import AxisHelper from '../chart_helpers/AxisHelper'
import AccessibilityBehavior from '../viz_behaviors/AccessibilityBehavior'
import BarChartViz from '../vizs/BarChartViz'
import BaseComponent from './BaseComponent'

/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * A bar chart is used to display values of a single variable represented by the height or length of lines or rectangles of equal width.
 *
 * ### Ejemplo de uso
 * ```javascript
 *   import {BarChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new BarChart($el, data, config);
 *
 * ```
 *
 * This chart is only intended to draw simple bar charts (vertical or horizontal) for stacked or grouped bar charts see [StackedBarChart](#/developer/StackedBarsComponent) and [GroupedBarChart](#/developer/GroupedBarsComponent)
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 2 columns. Each row represents a bar, and the first column should be the id/label of the bar, and the second column its value.
 *
 * | category | value  |
 * | -------- | ------ |
 * |   a      |    22.4|
 * |   b      |    52.3|
 * |   c      |    12.8|
 * |   d      |    26.0|
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Un gráfico de barras se utiliza para mostrar valores de una sola variable representada por la altura o longitud de líneas o rectángulos de igual ancho.
 *
 * ### Example Usage
 * ```javascript
 *   import {BarChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new BarChart($el, data, config);
 *
 * ```
 *
 * Este gráfico solo está destinado a dibujar gráficos de barras simples (verticales u horizontales) para gráficos de barras apiladas o agrupadas, consulta [Gráfico de barras apiladas](#/developer/StackedBarsComponent) y [Gráfico de barras agrupadas](#/developer/GroupedBarsComponent)
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 2 columnas. Cada fila representa una barra, y la primera columna debe ser el id/label de la barra y la segunda columna su valor.
 *
 * | categoria | valor  |
 * | -------- | ------ |
 * |   a      |    22.4|
 * |   b      |    52.3|
 * |   c      |    12.8|
 * |   d      |    26.0|
 *
 * {.data-table}
 */
export default class BarChartComponent extends BaseComponent {
  static componentLabel = 'Bar chart'
  static componentLabelEs = 'Gráfico de barras'
  static vizClass = BarChartViz
  static dataFormat = [
    { index: 0, axis: 'x0', types: [String], role: 'id', roleLabel: 'Category' },
    { index: 1, axis: 'y0', types: [Number], role: 'value', roleLabel: 'Value' },
  ]
  constructor($root, config, data) {
    super($root, config, data)
  }
  static defaultConfig = {
    title: this.componentLabel,
    AxisHelper: {
      x: {
        /**
         * {-EN-} Show/hide labels for each bar on the X axis.
         * {-ES-} Mostrar/ocultar etiquetas para cada barra en el eje X.
         */
        showTickValues: {
          value: false,
          default: false,
        },
      },
    },
    TooltipBehavior: {
      axis: {
        y: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
    },
    AccessibilityBehavior: {
      altTitle: this.componentLabel,
    },
  }
  static accessibilityRulesTotalItems = Object.values(super.accessibilityRules).length
  static accessibilityRules = {
    ...super.accessibilityRules,
    xShowTickValue: {
      id: 'xShowTickValue',
      order: this.accessibilityRulesTotalItems + 1,
      label: 'rule_x_showTickValue_barchart_label',
      description: 'rule_x_showTickValue_barchart_desc',
      sidebarFieldName: 'showBarLabels',
      check: (config, data) => {
        return config.AxisHelper?.x?.showTickValues
      },
      applyAutoConfigChange: function (config, data) {
        if (this.check(config, data)) return {}
        let newConfig = {
          AxisHelper: {
            x: {
              showTickValues: true,
            },
          },
        }
        return {
          oldConfig: {
            AxisHelper: { ...config.AxisHelper },
          },
          newConfig: newConfig,
        }
      },
      conditionalRules: ['textures'],
    },
    textures: {
      ...super.accessibilityRules.textures,
      order: this.accessibilityRulesTotalItems + 2,
      conditionalRules: ['xShowTickValue'],
    },
  }
}
